<script setup lang="ts" generic="TValue extends string">
import { computed } from 'vue'
import { SegmentGroup } from '@ark-ui/vue'
import UiText from './UiText.vue'

export interface UiSegmentedControlOption<TValue extends string = string> {
    label: string
    value: TValue
    disabled?: boolean
}

const props = withDefaults(defineProps<{
    /** List of selectable segments */
    options: readonly UiSegmentedControlOption<TValue>[]
    /** Disable the whole control */
    disabled?: boolean
    /** Name attached to the underlying hidden inputs (form submission) */
    name?: string
    /** Root element id */
    id?: string
    /** Accessible name for the segmented-control group */
    ariaLabel?: string
    /** Element containing helper or error text for the group */
    ariaDescribedby?: string
    /** Whether the current value fails validation */
    invalid?: boolean
}>(), {
    options: () => [],
    disabled: false,
})

const model = defineModel<TValue>()

const gridStyle = computed(() => ({
    gridTemplateColumns: `repeat(${props.options.length || 1}, minmax(0, 1fr))`,
}))
</script>

<template>
    <SegmentGroup.Root
        :model-value="model"
        :disabled="props.disabled"
        :name="props.name"
        :id="props.id"
        :aria-label="props.ariaLabel"
        :aria-describedby="props.ariaDescribedby"
        :aria-invalid="props.invalid || undefined"
        class="grid w-full"
        :style="gridStyle"
        @update:model-value="(value) => { if (value !== null) model = value as TValue }"
    >
        <SegmentGroup.Item
            v-for="(option, index) in props.options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled || props.disabled"
            class="relative flex h-10 cursor-pointer items-center justify-center border border-se-gray-300 bg-se-white px-3 py-2 text-center text-se-dark transition-colors hover:bg-se-gray-100 focus-within:z-10 data-[state=checked]:z-10 data-[state=checked]:border-se-dark data-[state=checked]:bg-se-dark data-[state=checked]:text-se-white data-[state=checked]:hover:bg-se-dark data-disabled:cursor-not-allowed data-disabled:opacity-50 data-disabled:hover:bg-se-white"
            :class="[
                index === 0 ? 'rounded-l-lg' : '-ml-px',
                index === props.options.length - 1 ? 'rounded-r-lg' : '',
            ]"
        >
            <SegmentGroup.ItemText>
                <UiText as="span" variant="p" class="font-semibold" :text="option.label" />
            </SegmentGroup.ItemText>
            <SegmentGroup.ItemHiddenInput
                :aria-describedby="props.ariaDescribedby"
                :aria-invalid="props.invalid || undefined"
            />
        </SegmentGroup.Item>
    </SegmentGroup.Root>
</template>
